import { ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

type StepInputProps = {
  value: string | undefined;
  placeholder: "Min" | "Max";
  onChange: (value: string | undefined) => void;
  maxLength: number;
  max: number;
  step?: number;
};

export function StepInput({ value, placeholder, onChange, maxLength, max, step = 1 }: StepInputProps) {
  const handleStep = (direction: "up" | "down") => {
    const numValue = value ? parseFloat(value) : 0;
    const newValue = direction === "up" ? numValue + step : numValue - step;

    if (newValue >= 0 && newValue <= max) {
      onChange(newValue.toString());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-" || e.key === "e" || e.key === "+") {
      e.preventDefault();
    }

    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      handleStep(e.key === "ArrowUp" ? "up" : "down");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Allow empty value
    if (!newValue) {
      onChange(undefined);
      return;
    }

    // Only allow numbers and decimal point
    if (!/^\d*\.?\d*$/.test(newValue)) {
      return;
    }

    // Remove leading zeros only if there are multiple leading zeros
    const cleanedValue = newValue.replace(/^0+/, "").slice(0, maxLength);

    if (cleanedValue.length > maxLength) {
      return;
    }

    const numValue = parseFloat(cleanedValue);
    if (numValue >= 0 && numValue <= max) {
      onChange(cleanedValue);
    }
  };

  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        className="h-10 border-input pr-8"
        value={value ?? ""}
        maxLength={maxLength}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col p-1 text-muted-foreground">
        <Button
          variant="ghost"
          size="icon"
          className="h-3 w-3 p-1"
          onClick={() => handleStep("up")}
          aria-label="Increment value"
        >
          <ChevronUp className="h-2 w-2" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-3 w-3 p-1"
          onClick={() => handleStep("down")}
          aria-label="Decrement value"
        >
          <ChevronDown className="h-2 w-2" />
        </Button>
      </div>
    </div>
  );
}
